import type { PdfThemeId, PlanCapabilities } from '@/types';

/**
 * The PDF themes, as the product knows them — name, one line, and whether Pro
 * is needed. Renderer-free on purpose: Settings lists these and entitlement is
 * decided from them, and neither may pull `@react-pdf/renderer` (~1.4 MB) into
 * its bundle. What each theme looks like lives in `src/pdf/themes`, which reads
 * its name and plan from here so the two cannot disagree.
 */
export interface PdfThemeInfo {
  id: PdfThemeId;
  name: string;
  description: string;
  /** Selectable, and drawn, only when the plan has `premiumPdfThemes`. */
  isPro: boolean;
}

export const PDF_THEME_CATALOG: Readonly<Record<PdfThemeId, PdfThemeInfo>> = {
  aurora: {
    id: 'aurora', name: 'Aurora', isPro: false,
    description: 'The standard look: hairline rules and a single accent bar.',
  },
  slate: {
    id: 'slate', name: 'Slate', isPro: true,
    description: 'Modern — a full colour band across the top and a bold total.',
  },
  ink: {
    id: 'ink', name: 'Ink', isPro: true,
    description: 'Classic — strong rules and a dark table heading, like a ledger.',
  },
  editorial: {
    id: 'editorial', name: 'Editorial', isPro: true,
    description: 'Elegant — a centred letterhead, fine rules and generous space.',
  },
};

export const DEFAULT_PDF_THEME_ID: PdfThemeId = 'aurora';

/** In display order: the default first. */
export const PDF_THEME_IDS = Object.keys(PDF_THEME_CATALOG) as PdfThemeId[];

export const isPdfThemeId = (value: unknown): value is PdfThemeId =>
  typeof value === 'string' && Object.hasOwn(PDF_THEME_CATALOG, value);

/**
 * The theme a document may actually use. Anything unknown — a typo, a theme
 * from a newer build, a hand-edited record — is the default, and so is a Pro
 * theme on a plan without `premiumPdfThemes`. Never throws, whatever it is given.
 */
export function entitledPdfThemeId(
  requested: unknown,
  capabilities: Pick<PlanCapabilities, 'premiumPdfThemes'> | undefined,
): PdfThemeId {
  if (!isPdfThemeId(requested)) return DEFAULT_PDF_THEME_ID;
  if (PDF_THEME_CATALOG[requested].isPro && capabilities?.premiumPdfThemes !== true) return DEFAULT_PDF_THEME_ID;
  return requested;
}
